import React, { FC, Fragment, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FilesImagesUploadAreaAndTable from 'components/shared/files-images-upload-area-and-table/files-images-upload-area-and-table.component';
import { File as DenthubFile } from 'models/file';
import Loader from 'components/shared/loader/loader.component';
import { useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { deleteConstructionsFileTemplate } from 'apollo/templates/order';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import LinearProgressBarWithLabel from 'components/shared/linear-progress-bar-with-label/linear-progress-bar-with-label.component';
import axios, { AxiosProgressEvent, CancelTokenSource } from 'axios';
import { useUploadFailureHandler } from 'hooks/useUploadFailureHandler';
import { startUploadAttempt } from 'utils/upload-failure';
import { CONSTANTS } from 'config/constants';
import Cookies from 'js-cookie';

type ConstructionFilesProps = {
    files: DenthubFile[];
    orderId: string;
    onChange?: (files: DenthubFile[]) => void;
    isImportedOrder: boolean | undefined;
};
const ConstructionFiles: FC<ConstructionFilesProps> = ({
    files,
    onChange,
    orderId,
    isImportedOrder,
}: ConstructionFilesProps) => {
    const { t: translate } = useTranslation(['order-event', 'milling-jobs']);
    const [currentFiles, setCurrentFiles] = useState<DenthubFile[]>([]);
    const [filesSnackbar, setFilesSnackbar] = useState(false);
    const [deleteFileSnackbar, setDeleteFileSnackbar] = useState(false);
    const [progress, setProgress] = useState<number>(0);
    const [uploading, setUploading] = useState(false);
    const [_, setCancelToken] = useState<CancelTokenSource | null>(null);
    const { showBoundary } = useDenthubErrorBoundary();
    const handleUploadFailure = useUploadFailureHandler();

    useEffect(() => {
        setCurrentFiles(files);
    }, [files]);

    const [deleteConstructionsFile, { loading: fileDeleteLoading }] = useMutation(deleteConstructionsFileTemplate, {
        onCompleted: ({ deleteConstructionsFile }) => {
            const constructionsFilesUrls = deleteConstructionsFile.constructionsFilesUrls;
            setCurrentFiles(constructionsFilesUrls);
            setDeleteFileSnackbar(true);
            if (onChange) {
                onChange(constructionsFilesUrls);
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleChange = async (files: File[]) => {
        const attempt = startUploadAttempt(files ?? []);
        try {
            if (!files || files.length === 0) {
                return;
            }
            setFilesSnackbar(false);
            setUploading(true);
            const source = axios.CancelToken.source();
            setCancelToken(source);
            const formData = new FormData();
            for (const file of files) {
                formData.append('files', file, file.name);
            }
            const response = await axios.post(
                `${CONSTANTS.DOC_API_URI}/upload/orders/${orderId}/constructions/files`,
                formData,
                {
                    headers: {
                        Authorization: Cookies.get(CONSTANTS.TOKEN_KEY),
                    },
                    onUploadProgress: (ev: AxiosProgressEvent) => {
                        attempt.sentBytes = ev.loaded;
                        setProgress((ev.loaded / (ev.total || 100)) * 100);
                    },
                    cancelToken: source.token,
                },
            );
            if (response.status === 200 && response.data) {
                setCurrentFiles(response.data?.constructionsFilesUrls);
                setFilesSnackbar(true);
                setProgress(0);
                if (onChange) {
                    onChange(response.data?.constructionsFilesUrls);
                }
            }
        } catch (error) {
            handleUploadFailure(error, { upload: 'construction-files', attempt, retry: () => handleChange(files) });
        } finally {
            setProgress(0);
            setUploading(false);
        }
    };

    const handleDeleteFile = async (fileId: string) => {
        if (fileId) {
            setDeleteFileSnackbar(false);
            await deleteConstructionsFile({
                variables: {
                    fileId: fileId,
                    orderId: orderId,
                },
            });
        }
    };

    return (
        <Fragment>
            <FilesImagesUploadAreaAndTable
                files={currentFiles}
                onDeleteFile={handleDeleteFile}
                onFileChange={handleChange}
                versionCheck={!isImportedOrder}
            />
            {uploading ? <LinearProgressBarWithLabel progress={progress} /> : undefined}
            {fileDeleteLoading ? <Loader /> : undefined}
            {filesSnackbar ? (
                <AlertSnackbar type="success" message={translate('order-event:SUCCESS_FILES')} />
            ) : undefined}
            {deleteFileSnackbar ? (
                <AlertSnackbar type="success" message={translate('order-event:SUCCESS_DELETE_FILE')} />
            ) : undefined}
        </Fragment>
    );
};

export default ConstructionFiles;
